import React, { useMemo, useState } from "react";
import { useQuery } from "react-query";
import { Avatar, Empty, Input, Modal, Select, Spin, Progress } from "antd";
import {
  AppstoreOutlined,
  ArrowRightOutlined,
  FilterOutlined,
  OrderedListOutlined,
  PlusOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { ProjectApi } from "../../services";
import { useDebounce } from "../../hooks";
import EmptyState from "../EmptyState";
import ModalTitle from "../ModalTitle";
import { makeShortcutId } from "../../utils/quickAccess";
import { PROJECT_TARGETS } from "./sources";
import styles from "./styles.module.css";

const AddShortcutModal = ({ open, onClose, onAdd, existingIds }) => {
  const [project, setProject] = useState(null);
  const [query, setQuery] = useDebounce();
  const [taskQuery, setTaskQuery] = useDebounce();

  const close = () => {
    setProject(null);
    onClose();
  };

  const { data: projects, isFetching: projectsLoading } = useQuery({
    queryKey: ["[QuickAccess] Projects", query],
    queryFn: () =>
      ProjectApi.getProjects({
        params: { per_page: 24, search: query || undefined },
      }).then((r) => r?.data?.projects ?? []),
    initialData: [],
    enabled: open && !project,
  });

  const { data: views, isFetching: viewsLoading } = useQuery({
    queryKey: ["[QuickAccess] Project Views", project?.uuid],
    queryFn: () =>
      ProjectApi.getProjectViews({
        params: { id: project.uuid, type: 1 },
      }).then((r) => r?.data?.views ?? []),
    initialData: [],
    enabled: !!project?.uuid,
  });

  const { data: tasks, isFetching: tasksLoading } = useQuery({
    queryKey: ["[QuickAccess] Project Tasks", project?.uuid, taskQuery],
    queryFn: () =>
      ProjectApi.getProjectTasks({
        params: {
          id: project.uuid,
          per_page: 20,
          search: taskQuery || undefined,
        },
      }).then((r) => r?.data?.tasks ?? []),
    initialData: [],
    enabled: !!project?.uuid,
  });

  const projectBase = project ? `/projects/edit/${project.uuid}` : "";

  const add = (shortcut) => {
    onAdd({ ...shortcut, avatar: project?.main_image?.original_url });
    close();
  };

  const isPinned = (id) => existingIds.includes(id);

  const taskOptions = useMemo(
    () =>
      (tasks ?? []).map((t) => ({
        value: t.uuid,
        label: t.title,
      })),
    [tasks],
  );

  return (
    <Modal
      open={open}
      onCancel={close}
      footer={null}
      width={640}
      centered
      className="ds-modal"
      destroyOnClose
      title={
        <ModalTitle
          icon={<PlusOutlined />}
          title={project ? project.title : "افزودن میان‌بر"}
          subtitle={
            project
              ? "کدوم قسمت این پروژه رو سنجاق کنیم؟"
              : "اول پروژه رو انتخاب کن"
          }
        />
      }
    >
      {!project ? (
        <div className={styles.picker}>
          <Input
            allowClear
            size="large"
            prefix={<SearchOutlined />}
            placeholder="جست‌وجوی پروژه"
            onChange={(e) => setQuery(e.target.value)}
          />

          <Spin spinning={projectsLoading}>
            {projects?.length === 0 ? (
              <EmptyState compact filtered />
            ) : (
              <div className={styles.projectGrid}>
                {projects.map((p) => {
                  const progress = Math.min(
                    100,
                    Math.max(0, parseInt(p.progress) || 0),
                  );
                  return (
                    <button
                      type="button"
                      key={p.uuid}
                      className={styles.projectCard}
                      onClick={() => setProject(p)}
                    >
                      <Avatar
                        size={40}
                        shape="square"
                        src={p.main_image?.original_url}
                        icon={<AppstoreOutlined />}
                      />
                      <span className={styles.projectName} title={p.title}>
                        {p.title}
                      </span>
                      <Progress
                        percent={progress}
                        size="small"
                        showInfo={false}
                      />
                    </button>
                  );
                })}
              </div>
            )}
          </Spin>
        </div>
      ) : (
        <div className={styles.picker}>
          <button
            type="button"
            className={styles.backLink}
            onClick={() => setProject(null)}
          >
            <ArrowRightOutlined />
            بازگشت به پروژه‌ها
          </button>

          <div>
            <div className={styles.pickerGroup}>صفحه‌های پروژه</div>
            <div className={styles.targetGrid}>
              {PROJECT_TARGETS.map((target) => {
                const id = makeShortcutId(
                  "project-section",
                  `${project.uuid}:${target.tab}`,
                );
                return (
                  <button
                    type="button"
                    key={target.tab}
                    className={styles.targetCard}
                    disabled={isPinned(id)}
                    onClick={() =>
                      add({
                        id,
                        sourceKey: "project-section",
                        label: `${project.title} · ${target.label}`,
                        url: `${projectBase}?tab=${target.tab}`,
                      })
                    }
                  >
                    <span
                      className={`${styles.targetIcon} kpi-icon-${target.tone}`}
                    >
                      {target.icon}
                    </span>
                    <span className={styles.targetLabel}>{target.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <div className={styles.pickerGroup}>ویوهای ذخیره‌شده</div>
            <Spin spinning={viewsLoading}>
              {views?.length === 0 ? (
                <div className={styles.pickerHint}>
                  این پروژه هنوز ویوی ذخیره‌شده‌ای نداره.
                </div>
              ) : (
                <div className={styles.targetGrid}>
                  {views.map((v) => {
                    const id = makeShortcutId(
                      "project-view",
                      `${project.uuid}:${v.id}`,
                    );
                    return (
                      <button
                        type="button"
                        key={v.id}
                        className={styles.targetCard}
                        disabled={isPinned(id)}
                        onClick={() =>
                          add({
                            id,
                            sourceKey: "project-view",
                            label: `${project.title} · ${v.title}`,
                            url: `${projectBase}?tab=Kanban&view=${v.id}`,
                          })
                        }
                      >
                        <span className={`${styles.targetIcon} kpi-icon-teal`}>
                          <FilterOutlined />
                        </span>
                        <span className={styles.targetLabel}>{v.title}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </Spin>
          </div>

          <div>
            <div className={styles.pickerGroup}>یک تسک مشخص</div>
            <Select
              className="w-100"
              size="large"
              showSearch
              placeholder="جست‌وجوی تسک"
              filterOption={false}
              onSearch={setTaskQuery}
              value={null}
              notFoundContent={
                tasksLoading ? <Spin size="small" /> : <Empty />
              }
              options={taskOptions}
              onChange={(uuid) => {
                const task = (tasks ?? []).find((t) => t.uuid === uuid);
                if (!task) return;
                add({
                  id: makeShortcutId("task", task.uuid),
                  sourceKey: "task",
                  label: task.title,
                  url: `${projectBase}?tab=Kanban&task=${task.uuid}`,
                });
              }}
              suffixIcon={<OrderedListOutlined />}
            />
          </div>
        </div>
      )}
    </Modal>
  );
};

export default AddShortcutModal;
